'use client';

import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, SxProps, Theme, Typography } from '@mui/material';

interface MarketButtonProps {
  label: string;
  price: string;
  line?: string | number;
  onClick: (e: React.MouseEvent) => void;
  active?: boolean;
  color?: string;
  priceChangeDirection?: 'up' | 'down' | null;
  lastUpdateTimestamp?: number;
  sx?: SxProps<Theme>;
}

/**
 * MarketButton Component
 * A styled button used for displaying betting outcomes (price and label)
 * within the sports game cards.
 */
export const MarketButton = ({
  label,
  price,
  onClick,
  line = '',
  active = false,
  color = 'primary.main',
  priceChangeDirection,
  lastUpdateTimestamp,
  sx,
}: MarketButtonProps) => {
  return (
    <Button
      onClick={onClick}
      variant="contained"
      sx={{
        flex: { xs: 1, sm: 1 },
        width: '100%',
        minWidth: { xs: 64, sm: 120, md: 120 },
        height: { xs: 34, sm: 42 },
        bgcolor: active ? color : 'action.selected',
        color: active ? '#ffffff' : 'text.primary',
        borderRadius: 1,
        textTransform: 'none',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        px: { xs: 0.5, sm: 1.5, md: 2 },
        py: { xs: 0.5, sm: 1 },
        alignItems: 'center',
        border: '1px solid',
        borderColor: active ? color : 'divider',
        position: 'relative',
        ...(Array.isArray(sx) ? sx : [sx]),
        boxShadow: (theme) => {
          const shadowDepth = { xs: '2px', sm: '4px' };
          return active
            ? theme.palette.mode === 'dark'
              ? `0 ${shadowDepth.xs} 0 color-mix(in srgb, ${color} 60%, black), 0 1px 4px rgba(0,0,0,0.4)`
              : `0 ${shadowDepth.xs} 0 color-mix(in srgb, ${color} 60%, black), 0 1px 4px rgba(0,0,0,0.2)`
            : theme.palette.mode === 'dark'
              ? `0 ${shadowDepth.xs} 0 rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)`
              : `0 ${shadowDepth.xs} 0 rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)`;
        },
        '@media (min-width: 600px)': {
          boxShadow: (theme: Theme) =>
            active
              ? theme.palette.mode === 'dark'
                ? `0 4px 0 color-mix(in srgb, ${color} 60%, black), 0 1px 4px rgba(0,0,0,0.4)`
                : `0 4px 0 color-mix(in srgb, ${color} 60%, black), 0 1px 4px rgba(0,0,0,0.2)`
              : theme.palette.mode === 'dark'
                ? '0 4px 0 rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)'
                : '0 4px 0 rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
        },
        transform: 'translateY(0px)',
        transition:
          'transform 0.12s ease, box-shadow 0.12s ease, background-color 0.12s ease, opacity 0.12s ease',
        '&:hover': {
          bgcolor: active ? color : 'action.hover',
          borderColor: active ? color : 'divider',
          opacity: active ? 0.92 : 1,
          transform: { xs: 'translateY(1px)', sm: 'translateY(2px)' },
          boxShadow: '0 1px 0 rgba(0,0,0,0.15)',
        },
        '&:active': {
          transform: { xs: 'translateY(2px)', sm: 'translateY(4px)' },
          boxShadow: '0 0px 0 rgba(0,0,0,0.1)',
        },
      }}
    >
      {priceChangeDirection && lastUpdateTimestamp && (
        <Box
          key={lastUpdateTimestamp}
          sx={{
            position: 'absolute',
            right: 4,
            bottom: priceChangeDirection === 'down' ? 4 : 'auto',
            top: priceChangeDirection === 'up' ? 4 : 'auto',
            rotate: priceChangeDirection === 'up' ? '0deg' : '90deg',
            width: 8,
            height: 8,
            borderTop: '2px solid',
            borderRight: '2px solid',
            borderColor: active ? '#ffffff' : color,
            opacity: 0.9,
            // Stay solid for 4s, then fade out over 1s
            animation: 'indicatorFadeOut 5s forwards',
            '@keyframes indicatorFadeOut': {
              '0%': { opacity: 1, transform: 'scale(1)' },
              '80%': { opacity: 1, transform: 'scale(1)' },
              '100%': { opacity: 0, transform: 'scale(0.5)' },
            },
          }}
        />
      )}

      <Stack direction="row" spacing={0.25} alignItems="baseline" sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: { xs: '0.6rem', sm: '0.7rem' },
            fontWeight: 600,
            opacity: 0.8,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        {line && (
          <Typography sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem' }, fontWeight: 600 }}>
            {line || '---'}
          </Typography>
        )}
      </Stack>

      <Typography
        sx={{
          fontSize: { xs: '0.75rem', sm: '1rem' },
          fontWeight: 700,
          ml: { xs: 0.25, sm: 0.5 },
          whiteSpace: 'nowrap',
        }}
      >
        {price || '---'}
      </Typography>
    </Button>
  );
};

export default MarketButton;
